import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { getApplicationSettings } from 'owa-application-settings';
import { fetch } from 'owa-fetch';
import { getGuid } from 'owa-guid';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';

interface TenantThemeResponse {
    LogoData?: string;
    LogoUrl?: string;
    HasTenantLogo?: boolean;
}

let cachedDarkLogoSrc: string | null = null;
let fetchPromise: Promise<string | null> | null = null;

const SAFE_LOGO_DATA_PREFIX = 'data:image/';

/**
 * Fetches the dark mode tenant logo from the webshell API.
 * Results are cached for the session to avoid redundant requests.
 * @returns The dark mode logo data URI, or null if unavailable
 */
export default async function fetchDarkModeTenantLogo(): Promise<string | null> {
    if (cachedDarkLogoSrc) {
        return cachedDarkLogoSrc;
    }

    if (fetchPromise) {
        return fetchPromise;
    }

    fetchPromise = fetchDarkModeTenantLogoInternal();
    return fetchPromise;
}

async function fetchDarkModeTenantLogoInternal(): Promise<string | null> {
    try {
        const webShellResourceUrl = getApplicationSettings(
            'PreAuthShellResourceUrls',
            getGlobalSettingsAccountMailboxInfo()
        ).webShellResourceUrl;

        if (!webShellResourceUrl) {
            return null;
        }

        const origin = new URL(webShellResourceUrl).origin;
        const token = await fetchWebShellToken(origin);

        if (!token) {
            return null;
        }

        const requestUrl = buildWebShellTenantThemeUrl(webShellResourceUrl);
        const fetchResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!fetchResponse.ok) {
            return null;
        }

        const response: TenantThemeResponse = await fetchResponse.json();

        if (response?.HasTenantLogo) {
            if (response.LogoData && isValidLogoDataUri(response.LogoData)) {
                cachedDarkLogoSrc = response.LogoData;
                return cachedDarkLogoSrc;
            }
            if (response.LogoUrl) {
                cachedDarkLogoSrc = response.LogoUrl;
                return cachedDarkLogoSrc;
            }
        }

        return null;
    } catch {
        return null;
    } finally {
        fetchPromise = null;
    }
}

async function fetchWebShellToken(resourceUrl: string): Promise<string> {
    let [token, tokenPromise] = getAccessTokenforResourceAsLazy(
        resourceUrl,
        'FetchDarkModeTenantLogo',
        getGuid(),
        undefined /* targetTenantId */,
        undefined /* wwwAuthenticateHeader */,
        undefined /* showFullTokenResponse */,
        undefined /* preferIdpToken */,
        getGlobalSettingsAccountMailboxInfo()
    );

    if (!token) {
        token = (await tokenPromise) as string;
    }

    if (typeof token === 'object' && token?.accessToken) {
        return token.accessToken;
    }

    if (typeof token === 'string') {
        return token;
    }

    return '';
}

function buildWebShellTenantThemeUrl(webShellResourceUrl: string): string {
    const origin = new URL(webShellResourceUrl).origin;
    const url = new URL('/api/theme/tenant', origin);
    url.searchParams.set('useDarkMode', 'true');
    return url.toString();
}

/**
 * Validates that the logo data is a safe data:image/ URI.
 * Rejects any other scheme or format to prevent cross-origin loads or unsafe schemes.
 */
function isValidLogoDataUri(logoData: string): boolean {
    return logoData.startsWith(SAFE_LOGO_DATA_PREFIX);
}
